import React, { useRef, useState, useEffect } from 'react';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import { Calendar, Building2, ChevronLeft, ChevronRight, Film } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';

const formatDate = (s, lang = 'es') => {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      const d = new Date(s);
      return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return s; }
  }
  return s;
};

const WorksGallery = () => {
  const { works } = useSite();
  const { t, lang } = useLang();
  const list = works || [];
  const scrollerRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  const updateScrollButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 10,
      right: el.scrollLeft < el.scrollWidth - el.clientWidth - 10,
    });
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [list]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.offsetWidth || 280;
    el.scrollBy({ left: dir * (cardWidth + 20) * 2, behavior: 'smooth' });
  };

  if (list.length === 0) return null;

  return (
    <section id="trabajos" className="relative py-20 sm:py-24 bg-[#0d1b2a] border-y border-white/5 overflow-hidden">
      <AnimatedBackground variant="aurora" intensity={0.28} />
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#00e0c7]/[0.04] blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10 sm:mb-12">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-3">
              {t('sections.works')}
            </div>
            <h3 className="font-display text-white text-2xl sm:text-3xl lg:text-[44px] font-light tracking-tight" style={{ lineHeight: 1.25 }}>
              {t('sections.worksTitle')}{' '}
              <span className="italic text-[#5eead4]">{t('sections.worksHighlight')}</span>.
            </h3>
            <p className="mt-4 text-white/55 text-[14px] sm:text-[15px] max-w-xl">
              {t('sections.worksDescription')}
            </p>
          </div>
          <div className="hidden md:block text-right">
            <div className="text-[#00e0c7] font-display text-5xl font-light">+500</div>
            <div className="text-white/40 text-[11px] uppercase tracking-widest">
              {t('sections.worksReleases')}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-end gap-2 mb-5">
          <button
            onClick={() => scrollBy(-1)}
            disabled={!canScroll.left}
            className="w-11 h-11 rounded-full border border-white/10 bg-white/[0.02] hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed text-white/70 hover:text-white transition-colors flex items-center justify-center"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            disabled={!canScroll.right}
            className="w-11 h-11 rounded-full border border-[#00e0c7]/30 bg-[#00e0c7]/10 hover:bg-[#00e0c7]/15 disabled:opacity-30 disabled:cursor-not-allowed text-[#00e0c7] transition-colors flex items-center justify-center"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div
            ref={scrollerRef}
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-2 px-2 no-scrollbar"
          >
            {list.map((w) => (
              <WorkCard key={w.id} work={w} lang={lang} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WorkCard = ({ work, lang }) => {
  const title = pickI18n(work.title, lang);
  const distributor = pickI18n(work.distributor, lang);
  const initials = (title || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();

  return (
    <div className="group flex-shrink-0 snap-start w-[230px] sm:w-[260px] rounded-2xl overflow-hidden border border-white/8 bg-white/[0.02] hover:border-[#00e0c7]/40 transition-all duration-500">
      <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-[#0a1622] to-[#142838]">
        {work.posterUrl ? (
          <img
            src={work.posterUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-5xl text-white/15 tracking-tighter">{initials}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1420] via-transparent to-transparent opacity-70" />

        <div className="absolute inset-0 bg-[#0d1b2a]/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#00e0c7] mb-2">
            <Building2 className="w-3 h-3" />
            {distributor}
          </div>
          {work.releaseDate && (
            <div className="flex items-center gap-2 text-[11px] text-white/70">
              <Calendar className="w-3 h-3" />
              {formatDate(work.releaseDate, lang)}
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        <h4 className="text-white font-medium text-[14px] leading-tight line-clamp-2 mb-1 min-h-[2.5em]">
          {title}
        </h4>
        <p className="text-white/45 text-[11px] truncate">{distributor}</p>
      </div>
    </div>
  );
};

export default WorksGallery;
